/** EFFET : navigation entre les écrans (accueil, solo, salle d'attente, partie). */
import { brokerEnv, iceConfig, turnServers } from './config';
import { openCamera } from './detection/faceTracker';
import type { Role } from './game/stateMachine';
import { generateRoomId, inviteLink } from './net/invite';
import { createPeerConnector } from './net/peer';
import { ConnectionError, type PeerLink } from './net/signaling';
import { confirmDialog } from './ui/confirm';
import { startGame, type GameHandle } from './ui/game';
import { renderHome } from './ui/home';
import { renderHostLobby, renderMessage, type LobbyChoice, type MessageAction } from './ui/lobby';
import { needsConfirmation, parseRoute, routeHash, type Route } from './ui/router';
import { renderTopbar } from './ui/shell';
import { renderSolo } from './ui/solo';
import { renderSmileySolo } from './ui/smileySolo';
import { renderDiagnostic } from './ui/diagnostic';

/**
 * Build e2e uniquement : `?cameraDelay=3000&cameraDelayFor=host` retarde
 * l'ouverture de la caméra d'un joueur (comme une demande d'autorisation
 * sur un vrai téléphone) pour tester les courses au démarrage.
 */
async function e2eCameraDelay(role: Role): Promise<void> {
  if (import.meta.env['VITE_E2E'] !== 'true') return;
  const params = new URLSearchParams(location.search);
  const ms = Number(params.get('cameraDelay') ?? 0);
  if (ms > 0 && params.get('cameraDelayFor') === role) {
    await new Promise((resolve) => setTimeout(resolve, ms));
  }
}

const GUEST_DEFAULT: LobbyChoice = { game: 'barbichette', mode: 'laugh' };

const errorText = (e: unknown): string =>
  e instanceof ConnectionError || e instanceof Error ? e.message : String(e);

const urlFor = (route: Route): string => location.pathname + location.search + routeHash(route);
const isSession = (route: Route): boolean => route.name === 'host' || route.name === 'join';

export function startApp(root: HTMLElement, topbar: HTMLElement): void {
  const connector = createPeerConnector(brokerEnv, iceConfig, turnServers);
  let current: Route = parseRoute(location.hash);
  let leave: (() => void) | null = null;
  let game: GameHandle | null = null;

  const cleanup = (): void => {
    leave?.();
    leave = null;
    game = null;
  };

  /** Point d'entrée unique de la navigation (boutons, en-tête, bouton retour). */
  const go = async (to: Route, fromHistory = false): Promise<void> => {
    if (needsConfirmation(current, to, game?.inProgress() ?? false)) {
      const ok = await confirmDialog('Quitter la partie ? Ton ami sera prévenu.', {
        confirm: 'Quitter',
        cancel: 'Rester',
      });
      if (!ok) {
        // Bouton retour du navigateur : on remet l'adresse de la partie.
        if (fromHistory) history.pushState(null, '', urlFor(current));
        return;
      }
      game?.quit();
    }
    if (isSession(current) && to.name !== current.name) {
      // Quitter une session réseau : rechargement complet, aucune connexion ne traîne.
      cleanup();
      history.replaceState(null, '', urlFor(to));
      location.reload();
      return;
    }
    if (!fromHistory) history.pushState(null, '', urlFor(to));
    show(to);
  };
  const goHome = (): void => void go({ name: 'home' });

  const play = async (link: PeerLink, role: Role, choice: LobbyChoice): Promise<void> => {
    // Départ propre : l'ami est prévenu tout de suite (sinon : heartbeat).
    addEventListener('pagehide', () => {
      link.close();
    });
    await e2eCameraDelay(role);
    const stream = await openCamera();
    cleanup();
    game = startGame(root, link, stream, { role, ...choice, onQuit: goHome });
    const handle = game;
    leave = () => {
      handle.stop();
      link.close();
      for (const t of stream.getTracks()) t.stop();
    };
  };

  const run = async (flow: () => Promise<[PeerLink, Role, LobbyChoice]>): Promise<void> => {
    try {
      await play(...(await flow()));
    } catch (e) {
      const actions: MessageAction[] = [
        { label: 'Retour à l’accueil', testid: 'error-home', onClick: goHome, secondary: true },
      ];
      // L'hôte est peut-être en train de se reconnecter : on laisse réessayer.
      if (e instanceof ConnectionError && e.code !== 'unsupported') {
        actions.push({ label: 'Réessayer', testid: 'retry', onClick: () => void run(flow) });
      }
      renderMessage(root, errorText(e), 'error', actions);
    }
  };

  const show = (route: Route): void => {
    cleanup();
    current = route;
    renderTopbar(topbar, route.name !== 'home', goHome);
    switch (route.name) {
      case 'home':
        renderHome(
          root,
          (game) => void go({ name: 'host', game }),
          () => void go({ name: 'solo' }),
          () => void go({ name: 'diagnostic' }),
          () => void go({ name: 'smiley-solo' }),
        );
        return;
      case 'smiley-solo':
        leave = renderSmileySolo(root, goHome);
        return;
      case 'diagnostic':
        leave = renderDiagnostic(root, goHome);
        return;
      case 'solo':
        leave = renderSolo(root, goHome);
        return;
      case 'host':
        void run(async () => {
          let choice: LobbyChoice = { game: route.game, mode: 'laugh' };
          const session = await connector.host(generateRoomId());
          const offLobby = renderHostLobby(
            root,
            inviteLink(location.href, session.roomId),
            choice,
            (c) => {
              choice = c;
            },
            session.signaling,
            goHome,
          );
          leave = () => {
            offLobby();
            session.close();
          };
          const link = await session.waitForGuest();
          offLobby();
          leave = null;
          return [link, 'host', choice];
        });
        return;
      case 'join':
        void run(async () => {
          renderMessage(root, 'Connexion à ton ami…');
          // Jeu et mode sont ceux de l'hôte : l'invité les apprend à la connexion (hello).
          return [await connector.join(route.roomId), 'guest', GUEST_DEFAULT];
        });
        return;
    }
  };

  addEventListener('popstate', () => void go(parseRoute(location.hash), true));
  show(current);
}
